import {Button, Flex, Title} from '@priority-software/priority-style-react';
import React, {ChangeEvent, useEffect, useRef} from 'react';
import {useEmailContext} from '../../../../../context';
import {texts} from '../../../../../texts';
import styles from '../../../../styles.module.scss';
import {useEventCallback} from 'usehooks-ts';
import {
    INSERT_IMAGE_COMMAND,
    InsertImagePayload
} from '../../../../../../../components/LexicalEditor/plugins/ImagesPlugin/commands';
import {readFileAsDataURL} from '../../../../../utils/readerFileAsDataURL';
import {createDataTestId} from '../../../../../../../utils/dataTestId';
import {checkImageSize} from '../../../../../../../components/LexicalEditor/utils/checkHtmlSize';
import {showFormClientSideMessage} from '../../../../../../Form/actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';

export const useImageCmp = () => {
    const {
        states: {emailEditor}
    } = useEmailContext();

    const fileInputRef = useRef(null);

    useEffect(() => {
        fileInputRef.current.accept = 'image/*';
    }, []);

    const renderHeader = () => (
        <Title
            componentClasses={styles.flex1}
            skin="h4"
            {...createDataTestId(`email-add-image-header-title`, true)}
        >
            {texts.image_title}
        </Title>
    );

    const InsertImagePayload = useEventCallback((payload: InsertImagePayload) => {
        emailEditor.dispatchCommand(INSERT_IMAGE_COMMAND, {...payload, isDraggable: false});
    });

    const handleButtonClick = useEventCallback(() => {
        fileInputRef.current.click();
    });

    const handleFileChange = useEventCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (!files || files.length === 0) return;

        const file = files[0];

        const isImageType = file.type.startsWith('image/');
        const hasImageExtension = /\.(jpe?g|png|gif)$/i.test(file.name);

        if (!isImageType || !hasImageExtension) {
            console.warn('Only image files are allowed.');
            // Reset the file input so the same file can be selected again
            showFormClientSideMessage('', priority.netitems.shared.Message.Type.Error, {
                content: texts.only_image_files_allowed,
                confirmButtonText: texts.check_mail_weight_btn
            });
            event.target.value = '';
            return;
        }

        try {
            const src = await readFileAsDataURL(file);
            const altText = file.name?.split?.('.')?.[0] ?? '';
            const isBiggerThanMaxSize = checkImageSize(src, altText, file.size);

            if (!isBiggerThanMaxSize) {
                InsertImagePayload({src, altText});
            }

            // Reset the file input so the same file can be selected again
            event.target.value = '';
        } catch (error) {
            console.error('Error reading file:', error);
            // Reset the file input in case of error too
            event.target.value = '';
        }
    });

    const renderBody = () => (
        <Flex
            componentClasses={styles.contentContainer}
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
        >
            <Title
                skin="h6"
                componentClasses={styles.textCenter}
                {...createDataTestId(`email-add-image-description`, true)}
            >
                {texts.drag_and_drop}
            </Title>
            <div {...createDataTestId(`email-add-image-button-container`, false)}>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{display: 'none'}}
                    onChange={handleFileChange}
                    {...createDataTestId(`email-add-image-button-input`, false)}
                />
                <Button
                    skin="secondary"
                    size="medium"
                    onClick={handleButtonClick}
                    {...createDataTestId(`email-add-image-button`, true)}
                >
                    {texts.select_file}
                </Button>
            </div>
        </Flex>
    );

    return {renderHeader, renderBody} as const;
};
